import { createContext, useContext, useState } from "react";
import { safeHref } from "../utils/safeHref";

export interface Branding {
  id: string;
  name: string;
  slug: string;
  logoUrl: string | null;
  faviconUrl: string | null;
  primaryColor: string | null;
  accentColor: string | null;
  contactEmail: string | null;
  website: string | null;
  // Present only when resolved by hostname (white-label custom domain).
  entraClientId: string | null;
  entraAuthority: string | null;
}

interface BrandingContextValue {
  branding: Branding | null;
  loading: boolean;
}

const BrandingContext = createContext<BrandingContextValue>({ branding: null, loading: false });

export function applyBrandingToRoot(b: Branding) {
  const root = document.documentElement;
  if (b.primaryColor) root.style.setProperty("--brand-primary", b.primaryColor);
  if (b.accentColor)  root.style.setProperty("--brand-accent",  b.accentColor);
  if (b.name)         document.title = `${b.name} Compliance Platform`;
  const favicon = safeHref(b.faviconUrl);
  if (favicon) {
    let link = document.querySelector<HTMLLinkElement>("link[rel~='icon']");
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }
    link.href = favicon;
  }
}

const API = import.meta.env.VITE_API_BASE_URL as string;

/**
 * Fetches branding for the current page load. Resolution order:
 *   1. VITE_CONSULTANCY_SLUG env var  (dedicated build per firm)
 *   2. ?slug= URL query param          (shared deployment, bookmarked)
 *   3. Current hostname                (custom-domain white-label portal)
 *
 * Returns null when no branding applies (i.e. the abacassecurity.com platform).
 * Exported so main.tsx can call it before React renders to configure MSAL.
 */
export async function fetchBranding(): Promise<Branding | null> {
  const envSlug = import.meta.env.VITE_CONSULTANCY_SLUG as string | undefined;
  const paramSlug = new URLSearchParams(window.location.search).get("slug");
  const slug = envSlug ?? paramSlug;

  if (slug) {
    try {
      const res = await fetch(`${API}/branding/public?slug=${encodeURIComponent(slug)}`);
      if (!res.ok) return null;
      const data = await res.json();
      return { ...data, entraClientId: null, entraAuthority: null };
    } catch {
      return null;
    }
  }

  // Hostname-based resolution — used when a consultancy's DNS CNAME points
  // portal.theirfirm.com at this deployment.
  const hostname = window.location.hostname;
  if (hostname === "localhost" || hostname.endsWith(".azurestaticapps.net")) {
    // Platform's own origins — no white-label branding to apply.
    return null;
  }

  try {
    const res = await fetch(`${API}/branding/public?host=${encodeURIComponent(hostname)}`);
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

interface BrandingProviderProps {
  children: React.ReactNode;
  /** Pre-fetched branding from main.tsx — avoids a duplicate network request. */
  initialBranding: Branding | null;
}

export function BrandingProvider({ children, initialBranding }: BrandingProviderProps) {
  const [branding] = useState<Branding | null>(() => {
    if (initialBranding) applyBrandingToRoot(initialBranding);
    return initialBranding;
  });

  return (
    <BrandingContext.Provider value={{ branding, loading: false }}>
      {children}
    </BrandingContext.Provider>
  );
}

export function useBranding() {
  return useContext(BrandingContext);
}
